import type { CategorisedResponse, FullActivityItem } from '@customTypes/types';
import { ResponsCategory, Role, WorkItemTypes } from '@customTypes/types';
import {
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  BriefcaseIcon,
  TicketIcon
} from '@heroicons/react/24/outline';
import {
  AppShell,
  Avatar,
  Badge,
  Box,
  Card,
  Divider,
  Grid,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Stepper,
  Text
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useProfileStore } from '@shared/store/profileStore.ts';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedDate } from 'react-intl';

import { useGetCategorisedActivityesListQuery } from '@/features/admin-panel/api/getCategorisedActivitiesList/hook';
import {
  getChatProfile,
  useCreateConversationMutation,
  useGetChatProfileQuery
} from '@/features/chat/api';
import { useChatStore } from '@/features/chat/store/chatStore';
import { ArtistFullProfile } from '@/features/work/view/artist-full-profile/ArtistFullProfile';
import { ClubFullProfile } from '@/features/work/view/club-full-profile/ClubFullProfile';
import { resolveImagePath } from '@/shared/helper';
import { LBLinkButton } from '@/shared/ui-kit/components';

import { Board } from './components/board/Board';

const columnOrder: ResponsCategory[] = [
  ResponsCategory.pending,
  ResponsCategory.accepted,
  ResponsCategory.contractOngoing,
  ResponsCategory.artistRequestedEndDate,
  ResponsCategory.clubSetEndDate,
  ResponsCategory.completed,
  ResponsCategory.notSuccessful
];

export const ResponsCategoryLabelsRu: Record<ResponsCategory, string> = {
  [ResponsCategory.pending]: 'В ожидании',
  [ResponsCategory.accepted]: 'Принято',
  [ResponsCategory.contractOngoing]: 'Контракт в процессе',
  [ResponsCategory.clubSetEndDate]: 'Клуб указал дату',
  [ResponsCategory.artistRequestedEndDate]: 'Артист попросил дату',
  [ResponsCategory.completed]: 'Завершено',
  [ResponsCategory.notSuccessful]: 'Неудачно'
};

const getStatusStep = (status?: string): number => {
  if (!status) return 0;

  const statusLower = status.toLowerCase();

  // Check for completed first (most specific)
  if (statusLower.includes('завершено') || statusLower.includes('completed')) return 4;

  // Check for not successful
  if (statusLower.includes('неудачно') || statusLower.includes('not successful')) return -1;

  // Check for end date setting
  if (statusLower.includes('дату окончания') || statusLower.includes('end date') || statusLower.includes('set end')) return 3;

  // Check for ongoing/in progress
  if (statusLower.includes('процессе') || statusLower.includes('ongoing') || statusLower.includes('contract')) return 2;

  // Check for accepted
  if (statusLower.includes('принято') || statusLower.includes('accepted')) return 1;

  // Check for pending
  if (statusLower.includes('ожидании') || statusLower.includes('pending')) return 0;

  return 0;
};

const getStatusColor = (status?: string): string => {
  if (!status) return 'gray';

  const statusLower = status.toLowerCase();

  if (statusLower.includes('завершено') || statusLower.includes('completed')) return 'green';
  if (statusLower.includes('неудачно') || statusLower.includes('not successful')) return 'red';
  if (statusLower.includes('дату окончания') || statusLower.includes('end date') || statusLower.includes('set end')) return 'grape';
  if (statusLower.includes('процессе') || statusLower.includes('ongoing') || statusLower.includes('contract')) return 'cyan';
  if (statusLower.includes('принято') || statusLower.includes('accepted')) return 'blue';
  if (statusLower.includes('ожидании') || statusLower.includes('pending')) return 'yellow';

  return 'gray';
};

export const AdminKanban = () => {
  const userActivity = useGetCategorisedActivityesListQuery({
    options: {
      refetchInterval: 60000,
      refetchIntervalInBackground: true,
      retry: 3,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 3000)
    }
  });
  const [opened, { open, close }] = useDisclosure();
  const [profileOpened, { open: profileOpen, close: profileClose }] =
    useDisclosure();
  const [selectedProfile, setSelectedProfile] = useState<{
    profileType: Role;
    profileId: number;
  }>();
  const navigate = useNavigate({ from: '/admin/kanban' });
  const { chatProfile, updateChatProfile } = useChatStore();
  const { profile, currentSessionId } = useProfileStore();

  const chatProfileQuery = useGetChatProfileQuery({
    profileType: profile?.role!,
    profileId: currentSessionId?.toString()!
  });

  useEffect(() => {
    if (chatProfileQuery.data?.data.chatProfileId) {
      updateChatProfile(chatProfileQuery.data.data);
    }
  }, [chatProfileQuery.data?.data?.chatProfileId, updateChatProfile]);

  const getCurrentConversation = useCreateConversationMutation();
  const [currentActivitiItem, setCurrentActivityItem] =
    useState<FullActivityItem>();
  const [participantChatProfiles, setParticipantChatProfiles] = useState<{
    artist: number;
    club: number;
  }>({ artist: 0, club: 0 });
  const [isResolvingChatProfiles, setIsResolvingChatProfiles] = useState(false);
  const [chatProfileError, setChatProfileError] = useState<string | null>(null);

  const resolveParticipantChatProfile = useCallback(
    async ({
      existingChatProfileId,
      profileId,
      role
    }: {
      existingChatProfileId?: number;
      profileId?: number;
      role: Role;
    }) => {
      if (existingChatProfileId && existingChatProfileId !== 0) {
        return existingChatProfileId;
      }

      if (!profileId) {
        return 0;
      }

      const response = await getChatProfile({
        params: {
          profileType: role,
          profileId: profileId.toString()
        }
      });

      return response.data.chatProfileId ?? 0;
    },
    []
  );

  useEffect(() => {
    if (!currentActivitiItem) {
      setParticipantChatProfiles({ artist: 0, club: 0 });
      setChatProfileError(null);
      setIsResolvingChatProfiles(false);
      return;
    }

    const initialArtistChatId = currentActivitiItem.artistChatProfileId ?? 0;
    const initialClubChatId = currentActivitiItem.clubChatProfileId ?? 0;

    setParticipantChatProfiles({
      artist: initialArtistChatId,
      club: initialClubChatId
    });

    const shouldResolveArtist =
      !initialArtistChatId && !!currentActivitiItem.artistProfileId;
    const shouldResolveClub =
      !initialClubChatId && !!currentActivitiItem.clubProfileId;

    if (!shouldResolveArtist && !shouldResolveClub) {
      setChatProfileError(null);
      setIsResolvingChatProfiles(false);
      return;
    }

    let isSubscribed = true;

    setIsResolvingChatProfiles(true);
    setChatProfileError(null);

    const fetchChatProfiles = async () => {
      try {
        const [artistChatId, clubChatId] = await Promise.all([
          shouldResolveArtist
            ? resolveParticipantChatProfile({
                existingChatProfileId: initialArtistChatId,
                profileId: currentActivitiItem.artistProfileId,
                role: Role.artist
              }).catch(() => {
                if (isSubscribed) {
                  setChatProfileError(
                    'Не удалось загрузить чат-профиль участника. Попробуйте обновить страницу.'
                  );
                }
                return 0;
              })
            : Promise.resolve(initialArtistChatId),
          shouldResolveClub
            ? resolveParticipantChatProfile({
                existingChatProfileId: initialClubChatId,
                profileId: currentActivitiItem.clubProfileId,
                role: Role.club
              }).catch(() => {
                if (isSubscribed) {
                  setChatProfileError(
                    'Не удалось загрузить чат-профиль участника. Попробуйте обновить страницу.'
                  );
                }
                return 0;
              })
            : Promise.resolve(initialClubChatId)
        ]);

        if (!isSubscribed) return;

        setParticipantChatProfiles({
          artist: artistChatId,
          club: clubChatId
        });
      } finally {
        if (isSubscribed) {
          setIsResolvingChatProfiles(false);
        }
      }
    };

    fetchChatProfiles().catch(() => {
      if (isSubscribed) {
        setChatProfileError(
          'Не удалось загрузить чат-профиль участника. Попробуйте обновить страницу.'
        );
      }
    });

    return () => {
      isSubscribed = false;
    };
  }, [currentActivitiItem, resolveParticipantChatProfile]);

  const onSelectItem = (activityItem: FullActivityItem) => {
    setCurrentActivityItem(activityItem);
    setParticipantChatProfiles({
      artist: activityItem.artistChatProfileId ?? 0,
      club: activityItem.clubChatProfileId ?? 0
    });
    setChatProfileError(null);
    open();
  };

  const sortedData = useMemo<CategorisedResponse>(() => {
    if (!userActivity.data?.data) return {} as CategorisedResponse;
    return Object.fromEntries(
      columnOrder.map((key) => [
        ResponsCategoryLabelsRu[key],
        userActivity.data.data[key] ?? []
      ])
    ) as CategorisedResponse;
  }, [userActivity.data?.data]);

  const adminChatProfileId =
    chatProfile?.chatProfileId ??
    chatProfileQuery.data?.data.chatProfileId ??
    0;

  const getChatButtonState = ({
    participantIds,
    requiresAdmin = true
  }: {
    participantIds: number[];
    requiresAdmin?: boolean;
  }) => {
    if (getCurrentConversation.isPending) {
      return {
        disabled: true,
        message: 'Открываем чат...',
        color: 'dimmed'
      } as const;
    }

    if (isResolvingChatProfiles || chatProfileQuery.isLoading) {
      return {
        disabled: true,
        message: 'Загружаем данные чата...',
        color: 'dimmed'
      } as const;
    }

    if (requiresAdmin && !adminChatProfileId) {
      if (chatProfileQuery.isError) {
        return {
          disabled: true,
          message: 'Не удалось загрузить чат-профиль администратора.',
          color: 'red'
        } as const;
      }

      return {
        disabled: true,
        message: 'Чат-профиль администратора ещё загружается.',
        color: 'dimmed'
      } as const;
    }

    if (participantIds.some((id) => !id)) {
      return {
        disabled: true,
        message:
          chatProfileError ??
          'Чат-профиль участника недоступен. Попробуйте позже.',
        color: 'red'
      } as const;
    }

    if (chatProfileError) {
      return {
        disabled: false,
        message: chatProfileError,
        color: 'red'
      } as const;
    }

    return { disabled: false, message: null, color: 'dimmed' } as const;
  };

  const artistChatState = getChatButtonState({
    participantIds: [participantChatProfiles.artist]
  });
  const clubChatState = getChatButtonState({
    participantIds: [participantChatProfiles.club]
  });
  const commonChatState = getChatButtonState({
    participantIds: [
      participantChatProfiles.club,
      participantChatProfiles.artist
    ],
    requiresAdmin: false
  });

  const getConversation = async (
    senderProfileId: number,
    recipientProfileId: number,
    isDirect: boolean = true
  ) => {
    if (!senderProfileId || !recipientProfileId) {
      return;
    }

    const conversation = await getCurrentConversation.mutateAsync({
      params: {
        senderProfileId: senderProfileId.toString(),
        recipientProfileId: recipientProfileId.toString(),
        isDirect
      }
    });

    navigate({
      to: '/admin/chat/conversation/$conversationId',
      params: { conversationId: conversation.data.conversationId.toString() }
    });
  };

  const conversationErrorMessage = 'Не удалось открыть чат. Попробуйте позже.';

  const openArtistChat = async () => {
    if (artistChatState.disabled) return;

    setChatProfileError(null);

    try {
      await getConversation(participantChatProfiles.artist, adminChatProfileId);
    } catch {
      setChatProfileError(conversationErrorMessage);
    }
  };

  const openClubChat = async () => {
    if (clubChatState.disabled) return;

    setChatProfileError(null);

    try {
      await getConversation(participantChatProfiles.club, adminChatProfileId);
    } catch {
      setChatProfileError(conversationErrorMessage);
    }
  };

  const openCommonChat = async () => {
    if (commonChatState.disabled) return;

    setChatProfileError(null);

    try {
      await getConversation(
        participantChatProfiles.club,
        participantChatProfiles.artist,
        false
      );
    } catch {
      setChatProfileError(conversationErrorMessage);
    }
  };

  const selectProfile = (profile: { profileType: Role; profileId: number }) => {
    setSelectedProfile(profile);
    profileOpen();
  };

  return (
    <AppShell.Main>
      <Modal
        withCloseButton
        opened={opened}
        size='xl'
        styles={{
          body: {
            padding: 0,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
          },
          content: {
            background: 'transparent',
            maxHeight: '90vh'
          },
          inner: {
            padding: '20px'
          }
        }}
        title={
          <Group gap='sm'>
            {currentActivitiItem?.workItemType && currentActivitiItem?.workItemId && (
              <>
                <Badge
                  size='lg'
                  variant='dot'
                  color={
                    currentActivitiItem?.workItemType === WorkItemTypes.Request
                      ? 'red'
                      : 'blue'
                  }
                  styles={{
                    label: {
                      fontSize: '16px',
                      fontWeight: 700,
                      textTransform: 'none'
                    }
                  }}
                >
                  {currentActivitiItem?.workItemType === WorkItemTypes.Request
                    ? `Запрос R-${currentActivitiItem?.workItemId}`
                    : `Приглашение I-${currentActivitiItem?.workItemId}`}
                </Badge>
                <Badge
                  color={getStatusColor(currentActivitiItem?.status)}
                  size='lg'
                  variant='light'
                  styles={{
                    label: {
                      fontSize: '14px',
                      fontWeight: 600,
                      textTransform: 'uppercase'
                    }
                  }}
                >
                  {currentActivitiItem?.status}
                </Badge>
              </>
            )}
          </Group>
        }
        onClose={close}
      >
        <Stack gap='md' p='md'>
            <Grid gutter='md'>
              {/* Artist Card */}
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card
                  withBorder
                  padding='md'
                  radius='md'
                  shadow='sm'
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.75)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.5)'
                  }}
                >
                  <Group gap='md' mb='md'>
                    <Avatar
                      size='xl'
                      src={resolveImagePath(currentActivitiItem?.artistPhoto)}
                    />
                    <Box style={{ flex: 1 }}>
                      <Text c='dimmed' fw={500} mb={6} size='xs' tt='uppercase'>
                        Артист
                      </Text>
                      <LBLinkButton
                        onClick={() =>
                          selectProfile({
                            profileType: Role.artist,
                            profileId: currentActivitiItem?.artistProfileId!
                          })
                        }
                      >
                        <Text fw={700} size='lg'>
                          {currentActivitiItem?.artistName}
                        </Text>
                      </LBLinkButton>
                    </Box>
                  </Group>

                  <Divider mb='md' />

                  <Stack gap='sm'>
                    <Box>
                      <Text c='dimmed' fw={500} mb={4} size='xs'>
                        Страна
                      </Text>
                      <Text fw={600} size='sm'>
                        {currentActivitiItem?.artistCountry || '-'}
                      </Text>
                    </Box>
                    <Box>
                      <Text c='dimmed' fw={500} mb={4} size='xs'>
                        Электронная почта
                      </Text>
                      <Text fw={600} size='sm' style={{ wordBreak: 'break-all' }}>
                        {currentActivitiItem?.artistEmail || '-'}
                      </Text>
                    </Box>
                    <Box>
                      <Text c='dimmed' fw={500} mb={4} size='xs'>
                        Телефон
                      </Text>
                      <Text fw={600} size='sm'>
                        {currentActivitiItem?.artistPhoneNumber || '-'}
                      </Text>
                    </Box>
                  </Stack>

                  <Divider my='md' />

                  <Box>
                    <LBLinkButton
                      disabled={artistChatState.disabled}
                      leftsection={<ChatBubbleLeftRightIcon width={16} />}
                      onClick={openArtistChat}
                    >
                      Перейти в чат
                    </LBLinkButton>
                    {artistChatState.message && (
                      <Text c={artistChatState.color} mt={6} size='xs'>
                        {artistChatState.message}
                      </Text>
                    )}
                  </Box>
                </Card>
              </Grid.Col>

              {/* Club Card */}
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card
                  withBorder
                  padding='md'
                  radius='md'
                  shadow='sm'
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.75)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.5)'
                  }}
                >
                  <Group gap='md' mb='md'>
                    <Avatar
                      size='xl'
                      src={resolveImagePath(currentActivitiItem?.clubPhoto)}
                    />
                    <Box style={{ flex: 1 }}>
                      <Text c='dimmed' fw={500} mb={6} size='xs' tt='uppercase'>
                        Клуб
                      </Text>
                      <LBLinkButton
                        onClick={() =>
                          selectProfile({
                            profileType: Role.club,
                            profileId: currentActivitiItem?.clubProfileId!
                          })
                        }
                      >
                        <Text fw={700} size='lg'>
                          {currentActivitiItem?.clubName}
                        </Text>
                      </LBLinkButton>
                    </Box>
                  </Group>

                  <Divider mb='md' />

                  <Stack gap='sm'>
                    <Box>
                      <Text c='dimmed' fw={500} mb={4} size='xs'>
                        Страна
                      </Text>
                      <Text fw={600} size='sm'>
                        {currentActivitiItem?.clubCountry || '-'}
                      </Text>
                    </Box>
                    <Box>
                      <Text c='dimmed' fw={500} mb={4} size='xs'>
                        Электронная почта
                      </Text>
                      <Text fw={600} size='sm' style={{ wordBreak: 'break-all' }}>
                        {currentActivitiItem?.clubEmail || '-'}
                      </Text>
                    </Box>
                    <Box>
                      <Text c='dimmed' fw={500} mb={4} size='xs'>
                        Телефон
                      </Text>
                      <Text fw={600} size='sm'>
                        {currentActivitiItem?.clubPhoneNumber || '-'}
                      </Text>
                    </Box>
                  </Stack>

                  <Divider my='md' />

                  <Box>
                    <LBLinkButton
                      disabled={clubChatState.disabled}
                      leftsection={<ChatBubbleLeftRightIcon width={16} />}
                      onClick={openClubChat}
                    >
                      Перейти в чат
                    </LBLinkButton>
                    {clubChatState.message && (
                      <Text c={clubChatState.color} mt={6} size='xs'>
                        {clubChatState.message}
                      </Text>
                    )}
                  </Box>
                </Card>
              </Grid.Col>
            </Grid>

            {/* Contract Details Card */}
            <Card
              withBorder
              padding='xl'
              radius='md'
              shadow='sm'
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.75)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.5)'
              }}
            >


              <Grid gutter='md'>
                <Grid.Col span={12}>
                  <Stack gap='xs' mb='md'>
                    <Text c='dimmed' size='xs'>
                      Статус
                    </Text>
                    {getStatusStep(currentActivitiItem?.status) === -1 ? (
                      <Box>
                        <Badge color='red' size='lg' variant='filled'>
                          Неудачно
                        </Badge>
                        <Text c='dimmed' mt='xs' size='xs'>
                          Этот контракт не был завершён
                        </Text>
                      </Box>
                    ) : (
                      <Stepper
                        color={getStatusColor(currentActivitiItem?.status)}
                        size='xs'
                        active={
                          getStatusStep(currentActivitiItem?.status) === 4
                            ? 5
                            : getStatusStep(currentActivitiItem?.status)
                        }
                        styles={{
                          step: {
                            padding: 0,
                            minWidth: 80
                          },
                          stepBody: {
                            marginTop: '4px'
                          },
                          stepLabel: {
                            fontSize: '11px',
                            fontWeight: 600,
                            lineHeight: 1.2
                          },
                          stepDescription: {
                            fontSize: '9px',
                            marginTop: '1px',
                            lineHeight: 1.2
                          }
                        }}
                      >
                        <Stepper.Step description='Ожидание' label='В ожидании' />
                        <Stepper.Step description='Одобрено' label='Принято' />
                        <Stepper.Step description='В процессе' label='В работе' />
                        <Stepper.Step description='Завершение' label='Дата окончания' />
                        <Stepper.Step description='Готово' label='Завершено' />
                      </Stepper>
                    )}
                  </Stack>
                </Grid.Col>

                <Grid.Col span={12}>
                  <Divider />
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 4 }}>
                  <Group gap='xs' mb={4}>
                    <BriefcaseIcon style={{ color: '#868e96' }} width={14} />
                    <Text c='dimmed' fw={500} size='xs'>
                      Роль
                    </Text>
                  </Group>
                  <Text fw={600} size='sm'>
                    {currentActivitiItem?.jobRole || '-'}
                  </Text>
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <Group gap='xs' mb={4}>
                    <CalendarIcon style={{ color: '#868e96' }} width={14} />
                    <Text c='dimmed' fw={500} size='xs'>
                      Дата начала
                    </Text>
                  </Group>
                  <Text fw={600} size='sm'>
                    {currentActivitiItem?.startDate ? (
                      <FormattedDate value={currentActivitiItem.startDate} />
                    ) : (
                      '-'
                    )}
                  </Text>
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <Group gap='xs' mb={4}>
                    <CalendarIcon style={{ color: '#868e96' }} width={14} />
                    <Text c='dimmed' fw={500} size='xs'>
                      Дата окончания
                    </Text>
                  </Group>
                  <Text fw={600} size='sm'>
                    {currentActivitiItem?.endDate ? (
                      <FormattedDate value={currentActivitiItem.endDate} />
                    ) : (
                      '-'
                    )}
                  </Text>
                </Grid.Col>

                <Grid.Col span={12}>
                  <Divider />
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Group gap='xs' mb={4}>
                    <CheckCircleIcon style={{ color: '#868e96' }} width={14} />
                    <Text c='dimmed' fw={500} size='xs'>
                      Прибытие подтверждено
                    </Text>
                  </Group>
                  <Badge
                    size='lg'
                    variant='light'
                    color={
                      currentActivitiItem?.arrivalConfirmed === true
                        ? 'teal'
                        : currentActivitiItem?.arrivalConfirmed === false
                          ? 'orange'
                          : 'gray'
                    }
                    leftSection={
                      currentActivitiItem?.arrivalConfirmed === true ? (
                        <CheckCircleIcon width={14} />
                      ) : null
                    }
                  >
                    {currentActivitiItem?.arrivalConfirmed === true
                      ? 'Да'
                      : currentActivitiItem?.arrivalConfirmed === false
                        ? 'Нет'
                        : 'Не указано'}
                  </Badge>
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Group gap='xs' mb={4}>
                    <TicketIcon style={{ color: '#868e96' }} width={14} />
                    <Text c='dimmed' fw={500} size='xs'>
                      Кредитация билета
                    </Text>
                  </Group>
                  <Badge
                    size='lg'
                    variant='light'
                    color={
                      currentActivitiItem?.ticketCreditation === true
                        ? 'teal'
                        : currentActivitiItem?.ticketCreditation === false
                          ? 'orange'
                          : 'gray'
                    }
                    leftSection={
                      currentActivitiItem?.ticketCreditation === true ? (
                        <TicketIcon width={14} />
                      ) : null
                    }
                  >
                    {currentActivitiItem?.ticketCreditation === true
                      ? 'Да'
                      : currentActivitiItem?.ticketCreditation === false
                        ? 'Нет'
                        : 'Не указано'}
                  </Badge>
                </Grid.Col>
              </Grid>

              <Divider my='md' />

              <Box>
                <Text c='dimmed' mb='xs' size='sm'>
                  Общий чат
                </Text>
                <LBLinkButton
                  disabled={commonChatState.disabled}
                  leftsection={<ChatBubbleLeftRightIcon width={16} />}
                  onClick={openCommonChat}
                >
                  Перейти в общий чат
                </LBLinkButton>
                {commonChatState.message && (
                  <Text c={commonChatState.color} mt={6} size='xs'>
                    {commonChatState.message}
                  </Text>
                )}
              </Box>
            </Card>
        </Stack>
      </Modal>
      <Modal opened={profileOpened} onClose={profileClose}>
        {selectedProfile?.profileType === Role.club && (
          <ClubFullProfile clubId={selectedProfile?.profileId.toString()} />
        )}

        {selectedProfile?.profileType === Role.artist && (
          <ArtistFullProfile
            artistProfileId={selectedProfile?.profileId.toString()}
          />
        )}
      </Modal>

      <Box
        style={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          minHeight: '90vh',
          padding: '20px'
        }}
      >
        <ScrollArea h='90vh'>
          {sortedData && <Board data={sortedData} onSelectItem={onSelectItem} />}
        </ScrollArea>
      </Box>
    </AppShell.Main>
  );
};
